/**
 * LoaderManager - JavaScript Implementation
 *
 * Simplified dynamic loader system for selecting themed loaders
 * based on seasonal events, location, and brand context.
 */

/**
 * LoaderManager Singleton
 */
class LoaderManager {
  constructor() {
    this.debugMode = false
    // Load global override from localStorage for persistence across pages
    this.manualOverride = this.loadGlobalOverride()
    this.loaderVariants = {
      valentines: {
        name: 'valentines',
        displayName: "Valentine's Day",
        description: "Simple Valentine's Day loader with a heart and cupid's arrow",
      },
      christmas: {
        name: 'christmas',
        displayName: 'Christmas Theme',
        description: 'Festive Christmas tree with ornaments',
      },
      halloween: {
        name: 'halloween',
        displayName: 'Halloween Theme',
        description: 'Spooky carved pumpkin with glowing eyes',
      },
      snowflake: {
        name: 'snowflake',
        displayName: 'Winter/Snowflake Theme',
        description: 'Elegant rotating ice crystals',
      },
      summer: {
        name: 'summer',
        displayName: 'Summer Theme',
        description: 'Bright sun with radiating energy',
      },
      newyear: {
        name: 'newyear',
        displayName: 'New Year Celebration',
        description: 'Fireworks bursting in "HNY" formation with golden sparkles',
      },
      fourthofjuly: {
        name: 'fourthofjuly',
        displayName: 'Fourth of July',
        description: 'Patriotic red, white, and blue fireworks with star centerpiece',
      },
      chinesenewyear: {
        name: 'chinesenewyear',
        displayName: 'Chinese New Year',
        description: 'Fire horse with golden flames and traditional Chinese character',
      },
      diwali: {
        name: 'diwali',
        displayName: 'Diwali Festival',
        description: 'Flickering diyas (oil lamps) with warm golden light particles',
      },
      default: {
        name: 'default',
        displayName: 'Hapana Default',
        description: 'Standard Hapana logo animation',
      },
    }
  }

  /**
   * Get singleton instance
   */
  static getInstance() {
    if (!LoaderManager.instance) {
      LoaderManager.instance = new LoaderManager()
    }
    return LoaderManager.instance
  }

  /**
   * Set debug mode
   */
  setDebugMode(enabled) {
    this.debugMode = enabled
  }

  /**
   * Load global override from localStorage
   */
  loadGlobalOverride() {
    try {
      const stored = localStorage.getItem('hapana-loader-override')
      return stored ? stored : null
    } catch (_error) {
      return null
    }
  }

  /**
   * Save global override to localStorage
   */
  saveGlobalOverride(loaderName) {
    try {
      if (loaderName) {
        localStorage.setItem('hapana-loader-override', loaderName)
      } else {
        localStorage.removeItem('hapana-loader-override')
      }
    } catch (error) {
      console.warn('LoaderManager: Could not save global override to localStorage', error)
    }
  }

  /**
   * Set manual override for testing (GLOBAL - affects ALL loaders across the app)
   */
  setManualOverride(loaderName) {
    this.manualOverride = loaderName
    this.saveGlobalOverride(loaderName)
    if (this.debugMode) {
      console.log(
        `LoaderManager: Global override set to ${loaderName} (affects ALL loaders across the app)`
      )
    }
  }

  /**
   * Clear manual override (GLOBAL - resets ALL loaders across the app)
   */
  clearOverride() {
    this.manualOverride = null
    this.saveGlobalOverride(null)
  }

  /**
   * Get loader variant based on context or override
   */
  getLoaderVariant(_context = {}) {
    if (this.manualOverride) {
      const overrideVariant = this.loaderVariants[this.manualOverride]
      if (overrideVariant) {
        if (this.debugMode) {
          console.log(`LoaderManager: Using manual override: ${this.manualOverride}`)
        }
        return overrideVariant
      }
    }

    // Simple seasonal detection based on current date
    const now = new Date()
    const month = now.getMonth() + 1 // 1-12
    const day = now.getDate()

    // New Year celebration (December 28 - January 7)
    if ((month === 12 && day >= 28) || (month === 1 && day <= 7)) {
      if (this.debugMode) {
        console.log('LoaderManager: New Year celebration detected')
      }
      return this.loaderVariants.newyear
    }

    // Chinese New Year season (January 21 - February 28)
    // Note: Chinese New Year varies each year, this covers most common dates
    if ((month === 1 && day >= 21) || (month === 2 && day <= 28)) {
      return this.loaderVariants.chinesenewyear
    }

    // Valentine's Day season (February 10-16)
    if (month === 2 && day >= 10 && day <= 16) {
      return this.loaderVariants.valentines
    }

    // Fourth of July celebration (June 30 - July 8)
    if ((month === 6 && day >= 30) || (month === 7 && day <= 8)) {
      return this.loaderVariants.fourthofjuly
    }

    // Halloween season (October 15-31, November 1-2)
    if ((month === 10 && day >= 15) || (month === 11 && day <= 2)) {
      return this.loaderVariants.halloween
    }

    // Diwali festival season (October 20 - November 15)
    // Note: Diwali varies each year, this covers most common dates
    if ((month === 10 && day >= 20) || (month === 11 && day <= 15)) {
      return this.loaderVariants.diwali
    }

    // Christmas season (December 1-31, January 1-7)
    if (month === 12 || (month === 1 && day <= 7)) {
      return this.loaderVariants.christmas
    }

    // Winter season (December-February, excluding Christmas)
    if (month === 12 || month === 1 || month === 2) {
      return this.loaderVariants.snowflake
    }

    // Summer season (June-August)
    if (month >= 6 && month <= 8) {
      return this.loaderVariants.summer
    }

    return this.loaderVariants.default
  }

  /**
   * Get list of all available variants
   */
  getAvailableVariants() {
    return Object.values(this.loaderVariants)
  }

  /**
   * Check if a variant exists
   */
  hasVariant(name) {
    return !!this.loaderVariants[name]
  }
}

export { LoaderManager }
